import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAudit, type Audit, type AuditFilter } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import AdminLayout from "./AdminLayout";

type Entry = Audit["entries"][number];

/** The change as a short line rather than raw JSON: "status: planned →
 * scheduled". The whole record is one click away for whoever needs it. */
function Changes({ changes }: { changes: Entry["changes"] }) {
  const [open, setOpen] = useState(false);
  if (changes === null || Object.keys(changes).length === 0) {
    return <span className="muted">—</span>;
  }
  const text = JSON.stringify(changes);
  if (text.length <= 80) {
    return <code className="audit-changes">{text}</code>;
  }
  return (
    <>
      <code className="audit-changes">{open ? text : `${text.slice(0, 80)}…`}</code>{" "}
      <button type="button" className="go-link" onClick={() => setOpen(!open)}>
        {open ? "less" : "more"}
      </button>
    </>
  );
}

/** The audit trail: every recorded change, newest first, one page at a
 * time. The filters live in the address, so a link to "what happened on
 * this site" is a link anyone can open. */
export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const filter: AuditFilter = {
    job: params.get("job") ?? "",
    actor: params.get("actor") ?? "",
    table: params.get("table") ?? "",
    since_days: params.get("since_days") ?? "",
    offset: Number(params.get("offset") ?? "0"),
  };
  const view = useAudit(filter);
  const data = view.data;
  function set(key: keyof AuditFilter, value: string) {
    const next = new URLSearchParams(params);
    if (value === "" || value === "0") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "offset") {
      next.delete("offset");
    }
    setParams(next);
  }
  return (
    <AdminLayout
      title="Audit"
      sub={data?.site ? `Every change on ${data.site.name}, who made it and when` : "Every change, who made it and when"}
    >
      <div className="toolbar">
        <select className="select" aria-label="Table" value={filter.table} onChange={(e) => set("table", e.target.value)}>
          <option value="">Every table</option>
          {(data?.tables ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select className="select" aria-label="Who" value={filter.actor} onChange={(e) => set("actor", e.target.value)}>
          <option value="">Anyone</option>
          {(data?.actors ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.email}
            </option>
          ))}
        </select>
        <input
          className="input audit-num"
          inputMode="numeric"
          aria-label="Last days"
          placeholder="Last n days"
          value={filter.since_days}
          onChange={(e) => set("since_days", e.target.value)}
        />
        <input
          className="input audit-num"
          inputMode="numeric"
          aria-label="Site id"
          placeholder="Site id"
          value={filter.job}
          onChange={(e) => set("job", e.target.value)}
        />
      </div>
      {view.isError ? <p className="notice bad">The audit trail could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading the audit trail…</div> : null}
      {data && data.entries.length === 0 ? (
        <div className="table-card empty-state">Nothing recorded for this filter.</div>
      ) : null}
      {data && data.entries.length > 0 ? (
        <div className="table-card">
          <table className="data-table audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Changes</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{formatDateTime(e.at)}</td>
                  <td>{e.actor ?? "system"}</td>
                  <td>
                    {e.action} {e.table} <span className="mono">#{e.row_id}</span>
                    {e.job_site_id ? (
                      <span className="sub">
                        <Link to={`/sites/${e.job_site_id}`}>site {e.job_site_id}</Link>
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <Changes changes={e.changes} />
                  </td>
                  <td>{e.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pager">
            <span className="pager-text">
              {data.total.toLocaleString("en-ZA")} entries · showing {data.offset + 1}–{Math.min(data.offset + data.limit, data.total)}
            </span>
            <button
              type="button"
              className="btn"
              disabled={data.offset === 0}
              onClick={() => set("offset", String(Math.max(0, data.offset - data.limit)))}
            >
              ← Newer
            </button>
            <button
              type="button"
              className="btn"
              disabled={data.offset + data.limit >= data.total}
              onClick={() => set("offset", String(data.offset + data.limit))}
            >
              Older →
            </button>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}
